// Types and pure helpers for the admin "Ingest and storage" page (ADR 0209).
// Kept free of React and of the API client so they can be tested in Node.

export interface IngestUsageDay {
  day: string;
  boatId: string;
  boatName: string | null;
  frames: number;
  frameBytes: number;
  samplesIn: number;
  acceptedRows: number;
  rejectedSamples: number;
  duplicatesSkipped: number;
  windowsResent: number;
  socketBytesIn: number;
  socketBytesOut: number;
  rawRows: number;
  quotaOverflow: number;
}

export interface StorageDay {
  day: string;
  kind: 'hypertable' | 'database';
  relation: string;
  tableBytes: number | null;
  indexBytes: number | null;
  toastBytes: number | null;
  totalBytes: number;
  /** When the sizes were measured (ISO). Each row is a snapshot, not a daily total. */
  recordedAt: string;
}

export interface IngestUsageSummary {
  windowDays: number;
  since: string;
  ingest: IngestUsageDay[];
  storage: StorageDay[];
}

/**
 * `HH:MM` of an ISO instant, in UTC, or '—' when the value is missing or not a
 * time (an API older than the page sends no `recordedAt`; rendering must not
 * throw on that).
 */
export function utcClock(iso: string | null | undefined): string {
  const ms = typeof iso === 'string' ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? '—' : new Date(ms).toISOString().slice(11, 16);
}

/** The rows of the newest recorded day, in the order the API returned them. */
export function latestStorageRows(storage: StorageDay[]): StorageDay[] {
  const day = storage[0]?.day;
  return day === undefined ? [] : storage.filter((s) => s.day === day);
}

/**
 * Heading for one day's storage rows. The sizes are a snapshot taken when the
 * job ran (nightly, or at boot on a day with no row yet), so the heading names
 * that moment, not only the day.
 */
export function storageHeading(rows: StorageDay[]): string {
  const first = rows[0];
  if (first === undefined) return 'Storage';
  const times = rows
    .map((r) => (typeof r.recordedAt === 'string' ? Date.parse(r.recordedAt) : NaN))
    .filter((ms) => !Number.isNaN(ms));
  if (times.length === 0) return `Storage recorded on ${first.day}`;
  const recordedAt = new Date(Math.max(...times)).toISOString();
  return `Storage at ${utcClock(recordedAt)} UTC on ${recordedAt.slice(0, 10)}, recorded nightly`;
}

/** A boat's label: its name, or the start of its id when it has none. */
export function boatLabel(row: Pick<IngestUsageDay, 'boatName' | 'boatId'>): string {
  return row.boatName ?? row.boatId.slice(0, 8);
}

export function sumOf(rows: IngestUsageDay[], pick: (r: IngestUsageDay) => number): number {
  return rows.reduce((n, r) => n + pick(r), 0);
}

/** One boat's socket messages and bytes for one day, per direction and type. */
export interface SocketBreakdownRow {
  direction: 'in' | 'out';
  /** The frame's `type`, or `outbox:<classKey>` for an outbox frame. */
  type: string;
  messages: number;
  bytes: number;
}

export interface SocketBreakdown {
  boatId: string;
  day: string;
  rows: SocketBreakdownRow[];
}

/** Identifies one row of the per-boat table: a UTC day and a boat. */
export function ingestRowKey(row: Pick<IngestUsageDay, 'day' | 'boatId'>): string {
  return `${row.day}|${row.boatId}`;
}

export function parseIngestRowKey(key: string): { day: string; boatId: string } | null {
  const [day, boatId] = key.split('|');
  return day !== undefined && boatId !== undefined ? { day, boatId } : null;
}

/** A row's share of all bytes in its direction that day, 0 to 1. */
export function directionShare(row: SocketBreakdownRow, rows: SocketBreakdownRow[]): number {
  const total = rows.filter((r) => r.direction === row.direction).reduce((n, r) => n + r.bytes, 0);
  return total === 0 ? 0 : row.bytes / total;
}
